import { texId } from './textures.ts';
import { boolProp, enumProp, intProp, type PropDef, type PropSpec, FACING4, FACING6 } from './state.ts';
import { Dir, type Direction } from './direction.ts';

/** How a block is turned into geometry by the mesher. */
export type RenderKind =
  | 'none' // invisible (air, structure voids, end portal is special-cased)
  | 'cube' // full 1x1x1 textured cube — the fast path
  | 'box' // one or more axis-aligned boxes from the model table
  | 'cross' // two diagonal quads (plants)
  | 'crop' // four parallel quads (wheat/carrots/potatoes)
  | 'liquid' // dynamic-height fluid surface
  | 'dust' // flat redstone wire with computed connections
  | 'rail' // flat rail sprite, possibly sloped
  | 'ladder' // flat quad on a wall
  | 'lily'; // flat quad near the floor

export type RenderLayer = 'opaque' | 'cutout' | 'translucent';

export type ToolKind = 'none' | 'pickaxe' | 'axe' | 'shovel' | 'hoe' | 'shears' | 'sword';

export interface TexFaces {
  down: number;
  up: number;
  north: number;
  south: number;
  west: number;
  east: number;
}

type TexInput =
  | string
  | { all?: string; top?: string; bottom?: string; side?: string; north?: string; south?: string; west?: string; east?: string; front?: string };

export interface BlockOptions {
  props?: readonly PropSpec[];
  /** Index into the state range that is the block's default state. */
  defaultProps?: Record<string, string>;
  render?: RenderKind;
  layer?: RenderLayer;
  tex?: TexInput | ((p: Record<string, string>) => TexInput);
  /** Full-cube collision. */
  solid?: boolean;
  /** Blocks all light and culls neighbouring faces. */
  opaque?: boolean;
  /** Extra light attenuation for non-opaque blocks (leaves/water are 1). */
  filter?: number;
  /** Light emitted, 0..15. May depend on state. */
  light?: number | ((p: Record<string, string>) => number);
  /** Seconds to break by hand at speed 1; -1 means unbreakable. */
  hardness?: number;
  /** Explosion resistance. */
  resistance?: number;
  tool?: ToolKind;
  /** Minimum tool tier for the block to drop anything. */
  tier?: number;
  /** True when the block yields nothing unless mined with the right tool. */
  requiresTool?: boolean;
  /** Can be replaced by placing a block into it (air, grass, water). */
  replaceable?: boolean;
  liquid?: boolean;
  /** Falls when unsupported (sand, gravel). */
  gravity?: boolean;
  /** 0 = fireproof; higher spreads faster. */
  flammable?: number;
  /** Blocks entity movement through it but is not a full cube (e.g. fence). */
  collides?: boolean;
  /** Faces of an adjacent identical block are culled (glass, water). */
  cullSame?: boolean;
  /** Provides a full solid top face that things can be placed on. */
  supports?: boolean;
  /** Emits/conducts redstone; see redstone/behaviour tables. */
  redstone?: boolean;
  /** Friction/slipperiness multiplier (ice is slippery). */
  slipperiness?: number;
  /** Block entity kind stored alongside the state, if any. */
  entity?: string;
  /** Sound family used for footsteps/breaking. */
  sound?: string;
  /** Item dropped instead of the block itself (name resolved lazily). */
  drops?: string | null;
  /** Blocks with this flag can be pushed but break instead of moving. */
  pushReaction?: 'normal' | 'destroy' | 'block' | 'push_only';
  /** Biome tint applied to all faces (grass blocks/leaves handled per-texture). */
  tintIndex?: number;
}

export interface BlockDef extends BlockOptions {
  id: number;
  name: string;
  propDefs: PropDef[];
  propByName: Map<string, PropDef>;
  stateCount: number;
  firstState: number;
  defaultState: number;
}

const defs: BlockDef[] = [];
const byName = new Map<string, BlockDef>();

function def(name: string, opts: BlockOptions = {}): BlockDef {
  if (byName.has(name)) throw new Error(`duplicate block ${name}`);
  const propDefs: PropDef[] = [];
  let stateCount = 1;
  for (const [pname, values] of opts.props ?? []) {
    propDefs.push({ name: pname, values, stride: stateCount });
    stateCount *= values.length;
  }
  const d: BlockDef = {
    ...opts,
    id: defs.length,
    name,
    propDefs,
    propByName: new Map(propDefs.map((p) => [p.name, p])),
    stateCount,
    firstState: 0,
    defaultState: 0,
  };
  defs.push(d);
  byName.set(name, d);
  return d;
}

// ---------------------------------------------------------------------------
// Shared property sets
// ---------------------------------------------------------------------------
const P_FACING6 = enumProp('facing', FACING6);
const P_FACING4 = enumProp('facing', FACING4);
const P_AXIS = enumProp('axis', ['x', 'y', 'z']);
const P_POWERED = boolProp('powered');
const P_LIT = boolProp('lit');
const P_HALF = enumProp('half', ['bottom', 'top']);
const P_SLAB = enumProp('type', ['bottom', 'top', 'double']);
const P_STAIR_SHAPE = enumProp('shape', [
  'straight',
  'inner_left',
  'inner_right',
  'outer_left',
  'outer_right',
]);
const P_CONNECT = [boolProp('north'), boolProp('south'), boolProp('west'), boolProp('east')] as const;
const P_OPEN = boolProp('open');
const P_HINGE = enumProp('hinge', ['left', 'right']);
const P_DOOR_HALF = enumProp('half', ['lower', 'upper']);
const P_LEVEL = intProp('level', 0, 15);

// ---------------------------------------------------------------------------
// Blocks
// ---------------------------------------------------------------------------

// Air must be block 0 / state 0 so that a zeroed Uint16Array is empty space.
export const AIR = def('air', { render: 'none', replaceable: true, hardness: 0, pushReaction: 'destroy' });
def('cave_air', { render: 'none', replaceable: true, hardness: 0, pushReaction: 'destroy' });
def('void_air', { render: 'none', replaceable: true, hardness: 0, pushReaction: 'destroy' });

const stoneLike = (name: string, tex: TexInput, hardness = 1.5, resistance = 6) =>
  def(name, {
    render: 'cube',
    tex,
    solid: true,
    opaque: true,
    hardness,
    resistance,
    tool: 'pickaxe',
    requiresTool: true,
    sound: 'stone',
  });

stoneLike('stone', 'stone').drops = 'cobblestone';
stoneLike('cobblestone', 'cobblestone', 2);
stoneLike('mossy_cobblestone', 'mossy_cobblestone', 2);
stoneLike('smooth_stone', 'smooth_stone', 2);
stoneLike('stone_bricks', 'stone_bricks', 1.5);
stoneLike('cracked_stone_bricks', 'cracked_stone_bricks', 1.5);
stoneLike('mossy_stone_bricks', 'mossy_stone_bricks', 1.5);
stoneLike('chiseled_stone_bricks', 'chiseled_stone_bricks', 1.5);
stoneLike('granite', 'granite');
stoneLike('diorite', 'diorite');
stoneLike('andesite', 'andesite');
stoneLike('tuff', 'tuff', 1.5);
stoneLike('calcite', 'calcite', 0.75);
stoneLike('bricks', 'bricks', 2);
stoneLike('terracotta', 'terracotta', 1.25);
stoneLike('quartz_block', 'quartz_block', 0.8);
stoneLike('blackstone', 'blackstone', 1.5);
stoneLike('basalt', { top: 'basalt_top', bottom: 'basalt_top', side: 'basalt_side' }, 1.25);
stoneLike('netherrack', 'netherrack', 0.4);
stoneLike('nether_bricks', 'nether_bricks', 2);
stoneLike('red_nether_bricks', 'red_nether_bricks', 2);
stoneLike('end_stone', 'end_stone', 3, 9);
stoneLike('end_stone_bricks', 'end_stone_bricks', 3, 9);
stoneLike('purpur_block', 'purpur_block', 1.5);
stoneLike('packed_ice', 'packed_ice', 0.5).slipperiness = 0.98;
stoneLike('magma_block', 'magma_block', 0.5).light = 3;

def('deepslate', {
  render: 'cube',
  props: [P_AXIS],
  defaultProps: { axis: 'y' },
  tex: (p) =>
    p.axis === 'y'
      ? { top: 'deepslate_top', bottom: 'deepslate_top', side: 'deepslate' }
      : p.axis === 'x'
        ? { west: 'deepslate_top', east: 'deepslate_top', top: 'deepslate', bottom: 'deepslate', north: 'deepslate', south: 'deepslate' }
        : { north: 'deepslate_top', south: 'deepslate_top', top: 'deepslate', bottom: 'deepslate', west: 'deepslate', east: 'deepslate' },
  solid: true,
  opaque: true,
  hardness: 3,
  resistance: 6,
  tool: 'pickaxe',
  requiresTool: true,
  drops: 'cobbled_deepslate',
  sound: 'deepslate',
});
stoneLike('cobbled_deepslate', 'cobbled_deepslate', 3.5);

def('bedrock', {
  render: 'cube',
  tex: 'bedrock',
  solid: true,
  opaque: true,
  hardness: -1,
  resistance: 3600000,
  pushReaction: 'block',
  sound: 'stone',
});
def('obsidian', {
  render: 'cube',
  tex: 'obsidian',
  solid: true,
  opaque: true,
  hardness: 50,
  resistance: 1200,
  tool: 'pickaxe',
  tier: 3,
  requiresTool: true,
  pushReaction: 'block',
  sound: 'stone',
});
def('crying_obsidian', {
  render: 'cube',
  tex: 'crying_obsidian',
  solid: true,
  opaque: true,
  hardness: 50,
  resistance: 1200,
  tool: 'pickaxe',
  tier: 3,
  requiresTool: true,
  light: 10,
  pushReaction: 'block',
  sound: 'stone',
});

// --- soils -----------------------------------------------------------------
def('dirt', { render: 'cube', tex: 'dirt', solid: true, opaque: true, hardness: 0.5, tool: 'shovel', sound: 'gravel' });
def('coarse_dirt', { render: 'cube', tex: 'coarse_dirt', solid: true, opaque: true, hardness: 0.5, tool: 'shovel', sound: 'gravel' });
def('rooted_dirt', { render: 'cube', tex: 'rooted_dirt', solid: true, opaque: true, hardness: 0.5, tool: 'shovel', sound: 'gravel' });
def('grass_block', {
  render: 'cube',
  tex: { top: 'grass_block_top', bottom: 'dirt', side: 'grass_block_side' },
  solid: true,
  opaque: true,
  hardness: 0.6,
  tool: 'shovel',
  drops: 'dirt',
  sound: 'grass',
});
def('podzol', {
  render: 'cube',
  tex: { top: 'podzol_top', bottom: 'dirt', side: 'grass_block_side' },
  solid: true,
  opaque: true,
  hardness: 0.5,
  tool: 'shovel',
  drops: 'dirt',
  sound: 'grass',
});
def('mycelium', {
  render: 'cube',
  tex: { top: 'mycelium_top', bottom: 'dirt', side: 'grass_block_side' },
  solid: true,
  opaque: true,
  hardness: 0.6,
  tool: 'shovel',
  drops: 'dirt',
  sound: 'grass',
});
def('sand', { render: 'cube', tex: 'sand', solid: true, opaque: true, hardness: 0.5, tool: 'shovel', gravity: true, sound: 'sand' });
def('red_sand', { render: 'cube', tex: 'red_sand', solid: true, opaque: true, hardness: 0.5, tool: 'shovel', gravity: true, sound: 'sand' });
def('gravel', { render: 'cube', tex: 'gravel', solid: true, opaque: true, hardness: 0.6, tool: 'shovel', gravity: true, sound: 'gravel' });
def('clay', { render: 'cube', tex: 'clay', solid: true, opaque: true, hardness: 0.6, tool: 'shovel', sound: 'gravel' });
def('mud', { render: 'cube', tex: 'mud', solid: true, opaque: true, hardness: 0.5, tool: 'shovel', sound: 'gravel' });
def('moss_block', { render: 'cube', tex: 'moss_block', solid: true, opaque: true, hardness: 0.1, tool: 'hoe', sound: 'grass' });
def('sandstone', {
  render: 'cube',
  tex: { top: 'sandstone_top', bottom: 'sandstone_top', side: 'sandstone' },
  solid: true,
  opaque: true,
  hardness: 0.8,
  tool: 'pickaxe',
  requiresTool: true,
  sound: 'stone',
});
def('red_sandstone', {
  render: 'cube',
  tex: { top: 'red_sand', bottom: 'red_sand', side: 'red_sandstone' },
  solid: true,
  opaque: true,
  hardness: 0.8,
  tool: 'pickaxe',
  requiresTool: true,
  sound: 'stone',
});
def('snow_block', { render: 'cube', tex: 'snow', solid: true, opaque: true, hardness: 0.2, tool: 'shovel', requiresTool: true, sound: 'snow' });
def('ice', {
  render: 'cube',
  tex: 'ice',
  solid: true,
  opaque: false,
  filter: 1,
  layer: 'translucent',
  cullSame: true,
  hardness: 0.5,
  tool: 'pickaxe',
  drops: null,
  slipperiness: 0.98,
  sound: 'glass',
});
def('snow_layer', {
  render: 'box',
  props: [intProp('layers', 1, 8)],
  tex: 'snow',
  hardness: 0.1,
  tool: 'shovel',
  requiresTool: true,
  drops: null,
  replaceable: true,
  collides: true,
  layer: 'opaque',
  sound: 'snow',
  pushReaction: 'destroy',
});
def('farmland', {
  render: 'box',
  props: [intProp('moisture', 0, 7)],
  tex: (p) => ({ top: Number(p.moisture) > 0 ? 'farmland_moist' : 'farmland', bottom: 'dirt', side: 'dirt' }),
  solid: false,
  collides: true,
  opaque: false,
  hardness: 0.6,
  tool: 'shovel',
  drops: 'dirt',
  sound: 'gravel',
});

// --- ores ------------------------------------------------------------------
const ore = (name: string, tex: string, tier: number, hardness = 3, light = 0) =>
  def(name, {
    render: 'cube',
    tex,
    solid: true,
    opaque: true,
    hardness,
    resistance: 3,
    tool: 'pickaxe',
    tier,
    requiresTool: true,
    light,
    sound: 'stone',
  });

ore('coal_ore', 'coal_ore', 1);
ore('iron_ore', 'iron_ore', 1);
ore('copper_ore', 'copper_ore', 1);
ore('gold_ore', 'gold_ore', 2);
ore('redstone_ore', 'redstone_ore', 2);
ore('lapis_ore', 'lapis_ore', 1);
ore('diamond_ore', 'diamond_ore', 2);
ore('emerald_ore', 'emerald_ore', 2);
ore('deepslate_coal_ore', 'deepslate_coal_ore', 1, 4.5);
ore('deepslate_iron_ore', 'deepslate_iron_ore', 1, 4.5);
ore('deepslate_copper_ore', 'deepslate_copper_ore', 1, 4.5);
ore('deepslate_gold_ore', 'deepslate_gold_ore', 2, 4.5);
ore('deepslate_redstone_ore', 'deepslate_redstone_ore', 2, 4.5);
ore('deepslate_lapis_ore', 'deepslate_lapis_ore', 1, 4.5);
ore('deepslate_diamond_ore', 'deepslate_diamond_ore', 2, 4.5);
ore('deepslate_emerald_ore', 'deepslate_emerald_ore', 2, 4.5);
ore('nether_quartz_ore', 'nether_quartz_ore', 1);
ore('nether_gold_ore', 'nether_gold_ore', 1);
def('ancient_debris', {
  render: 'cube',
  tex: { top: 'ancient_debris_top', bottom: 'ancient_debris_top', side: 'ancient_debris_side' },
  solid: true,
  opaque: true,
  hardness: 30,
  resistance: 1200,
  tool: 'pickaxe',
  tier: 3,
  requiresTool: true,
  sound: 'stone',
});

const mineralBlock = (name: string, tex: string, tier: number, hardness = 5) =>
  def(name, {
    render: 'cube',
    tex,
    solid: true,
    opaque: true,
    hardness,
    resistance: 6,
    tool: 'pickaxe',
    tier,
    requiresTool: true,
    sound: 'metal',
  });
mineralBlock('iron_block', 'iron_block', 1);
mineralBlock('gold_block', 'gold_block', 2);
mineralBlock('diamond_block', 'diamond_block', 2);
mineralBlock('emerald_block', 'emerald_block', 2);
mineralBlock('lapis_block', 'lapis_block', 1, 3);
mineralBlock('coal_block', 'coal_block', 1);
mineralBlock('copper_block', 'copper_block', 1, 3);
mineralBlock('netherite_block', 'netherite_block', 3, 50);

// --- wood ------------------------------------------------------------------
const WOODS = ['oak', 'spruce', 'birch', 'jungle', 'acacia', 'dark_oak'] as const;
export type WoodType = (typeof WOODS)[number];

for (const w of WOODS) {
  def(`${w}_log`, {
    render: 'cube',
    props: [P_AXIS],
    defaultProps: { axis: 'y' },
    tex: (p) =>
      p.axis === 'y'
        ? { top: `${w}_log_top`, bottom: `${w}_log_top`, side: `${w}_log` }
        : p.axis === 'x'
          ? { west: `${w}_log_top`, east: `${w}_log_top`, top: `${w}_log`, bottom: `${w}_log`, north: `${w}_log`, south: `${w}_log` }
          : { north: `${w}_log_top`, south: `${w}_log_top`, top: `${w}_log`, bottom: `${w}_log`, west: `${w}_log`, east: `${w}_log` },
    solid: true,
    opaque: true,
    hardness: 2,
    tool: 'axe',
    flammable: 5,
    sound: 'wood',
  });
  def(`${w}_planks`, {
    render: 'cube',
    tex: `${w}_planks`,
    solid: true,
    opaque: true,
    hardness: 2,
    resistance: 3,
    tool: 'axe',
    flammable: 5,
    sound: 'wood',
  });
  def(`${w}_leaves`, {
    render: 'cube',
    layer: 'cutout',
    tex: `${w}_leaves`,
    props: [intProp('distance', 1, 7), boolProp('persistent')],
    defaultProps: { distance: '7', persistent: 'false' },
    solid: true,
    opaque: false,
    filter: 1,
    hardness: 0.2,
    tool: 'shears',
    drops: null,
    flammable: 30,
    sound: 'grass',
  });
  def(`${w}_sapling`, {
    render: 'cross',
    layer: 'cutout',
    tex: `${w}_leaves`,
    props: [intProp('stage', 0, 1)],
    hardness: 0,
    sound: 'grass',
    pushReaction: 'destroy',
  });
}
for (const w of ['crimson', 'warped'] as const) {
  def(`${w}_stem`, {
    render: 'cube',
    props: [P_AXIS],
    defaultProps: { axis: 'y' },
    tex: (p) =>
      p.axis === 'y'
        ? { top: `${w}_stem_top`, bottom: `${w}_stem_top`, side: `${w}_stem` }
        : { top: `${w}_stem`, bottom: `${w}_stem`, side: `${w}_stem` },
    solid: true,
    opaque: true,
    hardness: 2,
    tool: 'axe',
    sound: 'wood',
  });
  def(`${w}_planks`, { render: 'cube', tex: `${w}_planks`, solid: true, opaque: true, hardness: 2, tool: 'axe', sound: 'wood' });
}
def('nether_wart_block', { render: 'cube', tex: 'nether_wart_block', solid: true, opaque: true, hardness: 1, tool: 'hoe', sound: 'grass' });
def('warped_wart_block', { render: 'cube', tex: 'warped_wart_block', solid: true, opaque: true, hardness: 1, tool: 'hoe', sound: 'grass' });
def('shroomlight', { render: 'cube', tex: 'shroomlight', solid: true, opaque: true, hardness: 1, light: 15, tool: 'hoe', sound: 'grass' });

// --- decorative ------------------------------------------------------------
def('glass', {
  render: 'cube',
  tex: 'glass',
  layer: 'cutout',
  solid: true,
  opaque: false,
  cullSame: true,
  hardness: 0.3,
  drops: null,
  sound: 'glass',
});
def('bookshelf', {
  render: 'cube',
  tex: { top: 'oak_planks', bottom: 'oak_planks', side: 'bookshelf' },
  solid: true,
  opaque: true,
  hardness: 1.5,
  tool: 'axe',
  flammable: 30,
  drops: 'book',
  sound: 'wood',
});
def('crafting_table', {
  render: 'cube',
  tex: {
    top: 'crafting_table_top',
    bottom: 'oak_planks',
    north: 'crafting_table_front',
    south: 'crafting_table_front',
    west: 'crafting_table_side',
    east: 'crafting_table_side',
  },
  solid: true,
  opaque: true,
  hardness: 2.5,
  tool: 'axe',
  flammable: 5,
  sound: 'wood',
});
def('furnace', {
  render: 'cube',
  props: [P_FACING4, P_LIT],
  defaultProps: { facing: 'north', lit: 'false' },
  tex: (p) => {
    const front = p.lit === 'true' ? 'furnace_front_on' : 'furnace_front';
    return facingTex(p.facing!, front, 'furnace_side', 'furnace_top', 'furnace_top');
  },
  light: (p) => (p.lit === 'true' ? 13 : 0),
  solid: true,
  opaque: true,
  hardness: 3.5,
  tool: 'pickaxe',
  requiresTool: true,
  entity: 'furnace',
  sound: 'stone',
});
def('chest', {
  render: 'box',
  props: [P_FACING4],
  defaultProps: { facing: 'north' },
  tex: (p) => facingTex(p.facing!, 'chest_front', 'chest_side', 'chest_top', 'chest_top'),
  solid: false,
  collides: true,
  opaque: false,
  hardness: 2.5,
  tool: 'axe',
  flammable: 0,
  entity: 'chest',
  sound: 'wood',
});
def('trapped_chest', {
  render: 'box',
  props: [P_FACING4],
  defaultProps: { facing: 'north' },
  tex: (p) => facingTex(p.facing!, 'chest_front', 'chest_side', 'chest_top', 'chest_top'),
  solid: false,
  collides: true,
  opaque: false,
  hardness: 2.5,
  tool: 'axe',
  entity: 'chest',
  redstone: true,
  sound: 'wood',
});
def('barrel', {
  render: 'cube',
  props: [P_FACING6],
  defaultProps: { facing: 'up' },
  tex: (p) =>
    p.facing === 'up'
      ? { top: 'barrel_top', bottom: 'barrel_top', side: 'barrel_side' }
      : { top: 'barrel_side', bottom: 'barrel_side', side: 'barrel_side' },
  solid: true,
  opaque: true,
  hardness: 2.5,
  tool: 'axe',
  entity: 'chest',
  sound: 'wood',
});
def('spawner', {
  render: 'cube',
  tex: 'spawner',
  layer: 'cutout',
  solid: true,
  opaque: false,
  hardness: 5,
  tool: 'pickaxe',
  requiresTool: true,
  drops: null,
  entity: 'spawner',
  sound: 'metal',
});
def('tnt', {
  render: 'cube',
  tex: { top: 'tnt_top', bottom: 'tnt_bottom', side: 'tnt_side' },
  solid: true,
  opaque: true,
  hardness: 0,
  flammable: 15,
  sound: 'grass',
});
def('slime_block', {
  render: 'cube',
  tex: 'slime_block',
  layer: 'translucent',
  solid: true,
  opaque: false,
  cullSame: true,
  hardness: 0,
  sound: 'slime',
});
def('honey_block', {
  render: 'cube',
  tex: 'honey_block',
  layer: 'translucent',
  solid: true,
  opaque: false,
  cullSame: true,
  hardness: 0,
  sound: 'slime',
});
def('sponge', { render: 'cube', tex: 'sponge', solid: true, opaque: true, hardness: 0.6, tool: 'hoe', sound: 'grass' });
def('hay_block', {
  render: 'cube',
  props: [P_AXIS],
  defaultProps: { axis: 'y' },
  tex: (p) => (p.axis === 'y' ? { top: 'hay_top', bottom: 'hay_top', side: 'hay_side' } : { top: 'hay_side', bottom: 'hay_side', side: 'hay_side' }),
  solid: true,
  opaque: true,
  hardness: 0.5,
  tool: 'hoe',
  sound: 'grass',
});
def('cobweb', {
  render: 'cross',
  tex: 'cobweb',
  layer: 'cutout',
  hardness: 4,
  tool: 'shears',
  drops: 'string',
  pushReaction: 'destroy',
  sound: 'wool',
});
def('ladder', {
  render: 'ladder',
  props: [P_FACING4],
  defaultProps: { facing: 'north' },
  tex: 'ladder',
  layer: 'cutout',
  hardness: 0.4,
  tool: 'axe',
  sound: 'ladder',
  pushReaction: 'destroy',
});
def('glowstone', { render: 'cube', tex: 'glowstone', solid: true, opaque: true, hardness: 0.3, light: 15, drops: 'glowstone_dust', sound: 'glass' });
def('soul_sand', {
  render: 'cube',
  tex: 'soul_sand',
  solid: true,
  opaque: true,
  hardness: 0.5,
  tool: 'shovel',
  slipperiness: 0.4,
  sound: 'sand',
});
def('soul_soil', { render: 'cube', tex: 'soul_soil', solid: true, opaque: true, hardness: 0.5, tool: 'shovel', sound: 'sand' });

const COLORS = [
  'white', 'orange', 'magenta', 'light_blue', 'yellow', 'lime', 'pink', 'gray',
  'light_gray', 'cyan', 'purple', 'blue', 'brown', 'green', 'red', 'black',
] as const;
export type DyeColor = (typeof COLORS)[number];
for (const c of COLORS) {
  def(`${c}_wool`, {
    render: 'cube',
    tex: `${c}_wool`,
    solid: true,
    opaque: true,
    hardness: 0.8,
    tool: 'shears',
    flammable: 30,
    sound: 'wool',
  });
}

// --- liquids ---------------------------------------------------------------
def('water', {
  render: 'liquid',
  props: [P_LEVEL],
  layer: 'translucent',
  tex: { all: 'water_still' },
  solid: false,
  opaque: false,
  filter: 1,
  liquid: true,
  replaceable: true,
  cullSame: true,
  hardness: -1,
  pushReaction: 'destroy',
});
def('lava', {
  render: 'liquid',
  props: [P_LEVEL],
  layer: 'opaque',
  tex: { all: 'lava_still' },
  solid: false,
  opaque: false,
  liquid: true,
  replaceable: true,
  cullSame: true,
  light: 15,
  hardness: -1,
  pushReaction: 'destroy',
});

// --- plants ----------------------------------------------------------------
const plant = (name: string, tex: string, extra: BlockOptions = {}) =>
  def(name, {
    render: 'cross',
    layer: 'cutout',
    tex,
    hardness: 0,
    replaceable: false,
    sound: 'grass',
    pushReaction: 'destroy',
    flammable: 60,
    ...extra,
  });

plant('short_grass', 'short_grass', { replaceable: true, drops: null, tool: 'shears' });
plant('fern', 'fern', { replaceable: true, drops: null, tool: 'shears' });
plant('dead_bush', 'dead_bush', { replaceable: true, drops: null, tool: 'shears' });
plant('dandelion', 'dandelion');
plant('poppy', 'poppy');
plant('blue_orchid', 'blue_orchid');
plant('allium', 'allium');
plant('cornflower', 'cornflower');
plant('oxeye_daisy', 'oxeye_daisy');
plant('brown_mushroom', 'brown_mushroom', { light: 1 });
plant('red_mushroom', 'red_mushroom');
def('tall_grass', {
  render: 'cross',
  layer: 'cutout',
  props: [P_DOOR_HALF],
  tex: (p) => (p.half === 'upper' ? 'tall_grass_top' : 'tall_grass_bottom'),
  hardness: 0,
  replaceable: true,
  drops: null,
  tool: 'shears',
  pushReaction: 'destroy',
  sound: 'grass',
});
def('sugar_cane', {
  render: 'cross',
  layer: 'cutout',
  props: [intProp('age', 0, 15)],
  tex: 'sugar_cane',
  hardness: 0,
  pushReaction: 'destroy',
  sound: 'grass',
});
def('cactus', {
  render: 'box',
  props: [intProp('age', 0, 15)],
  tex: { top: 'cactus_top', bottom: 'cactus_top', side: 'cactus_side' },
  solid: false,
  collides: true,
  opaque: false,
  hardness: 0.4,
  sound: 'wool',
  pushReaction: 'destroy',
});
def('vine', {
  render: 'box',
  layer: 'cutout',
  props: [...P_CONNECT, boolProp('up')],
  tex: 'vine',
  hardness: 0.2,
  tool: 'shears',
  drops: null,
  flammable: 15,
  pushReaction: 'destroy',
  sound: 'grass',
});
def('lily_pad', {
  render: 'lily',
  layer: 'cutout',
  tex: 'lily_pad',
  hardness: 0,
  pushReaction: 'destroy',
  sound: 'grass',
});
def('pumpkin', {
  render: 'cube',
  tex: { top: 'pumpkin_top', bottom: 'pumpkin_top', side: 'pumpkin_side' },
  solid: true,
  opaque: true,
  hardness: 1,
  tool: 'axe',
  sound: 'wood',
});
def('carved_pumpkin', {
  render: 'cube',
  props: [P_FACING4],
  defaultProps: { facing: 'north' },
  tex: (p) => facingTex(p.facing!, 'pumpkin_face', 'pumpkin_side', 'pumpkin_top', 'pumpkin_top'),
  solid: true,
  opaque: true,
  hardness: 1,
  tool: 'axe',
  sound: 'wood',
});
def('jack_o_lantern', {
  render: 'cube',
  props: [P_FACING4],
  defaultProps: { facing: 'north' },
  tex: (p) => facingTex(p.facing!, 'pumpkin_face', 'pumpkin_side', 'pumpkin_top', 'pumpkin_top'),
  solid: true,
  opaque: true,
  light: 15,
  hardness: 1,
  tool: 'axe',
  sound: 'wood',
});
def('melon', {
  render: 'cube',
  tex: { top: 'melon_top', bottom: 'melon_top', side: 'melon_side' },
  solid: true,
  opaque: true,
  hardness: 1,
  tool: 'axe',
  drops: null,
  sound: 'wood',
});
const crop = (name: string, stages: number, texFor: (age: number) => string) =>
  def(name, {
    render: 'crop',
    layer: 'cutout',
    props: [intProp('age', 0, stages - 1)],
    tex: (p) => texFor(Number(p.age)),
    hardness: 0,
    drops: null,
    pushReaction: 'destroy',
    sound: 'grass',
  });
crop('wheat', 8, (a) => (a >= 7 ? 'wheat_stage7' : a >= 3 ? 'wheat_stage3' : 'wheat_stage0'));
crop('carrots', 8, (a) => (a >= 7 ? 'carrots_stage7' : a >= 3 ? 'carrots_stage3' : 'carrots_stage0'));
crop('potatoes', 8, (a) => (a >= 7 ? 'potatoes_stage7' : a >= 3 ? 'potatoes_stage3' : 'potatoes_stage0'));
crop('nether_wart', 4, (a) => (a >= 2 ? 'nether_wart_stage2' : 'nether_wart_stage0'));

// --- light sources ---------------------------------------------------------
def('torch', {
  render: 'box',
  layer: 'cutout',
  tex: 'torch',
  light: 14,
  hardness: 0,
  pushReaction: 'destroy',
  sound: 'wood',
});
def('wall_torch', {
  render: 'box',
  layer: 'cutout',
  props: [P_FACING4],
  defaultProps: { facing: 'north' },
  tex: 'torch',
  light: 14,
  hardness: 0,
  drops: 'torch',
  pushReaction: 'destroy',
  sound: 'wood',
});
def('soul_torch', { render: 'box', layer: 'cutout', tex: 'soul_torch', light: 10, hardness: 0, pushReaction: 'destroy', sound: 'wood' });
def('lantern', {
  render: 'box',
  layer: 'cutout',
  props: [boolProp('hanging')],
  tex: 'lantern',
  light: 15,
  hardness: 3.5,
  tool: 'pickaxe',
  requiresTool: true,
  collides: true,
  sound: 'metal',
});
def('campfire', {
  render: 'box',
  layer: 'cutout',
  props: [P_FACING4, P_LIT],
  defaultProps: { facing: 'north', lit: 'true' },
  tex: 'campfire',
  light: (p) => (p.lit === 'true' ? 15 : 0),
  hardness: 2,
  tool: 'axe',
  collides: true,
  sound: 'wood',
});

// --- redstone --------------------------------------------------------------
def('redstone_wire', {
  render: 'dust',
  layer: 'cutout',
  props: [intProp('power', 0, 15)],
  tex: 'redstone_dust_line',
  hardness: 0,
  drops: 'redstone',
  redstone: true,
  pushReaction: 'destroy',
  sound: 'stone',
});
def('redstone_torch', {
  render: 'box',
  layer: 'cutout',
  props: [P_LIT],
  defaultProps: { lit: 'true' },
  tex: (p) => (p.lit === 'true' ? 'redstone_torch' : 'redstone_torch_off'),
  light: (p) => (p.lit === 'true' ? 7 : 0),
  hardness: 0,
  redstone: true,
  drops: 'redstone_torch',
  pushReaction: 'destroy',
  sound: 'wood',
});
def('redstone_wall_torch', {
  render: 'box',
  layer: 'cutout',
  props: [P_FACING4, P_LIT],
  defaultProps: { facing: 'north', lit: 'true' },
  tex: (p) => (p.lit === 'true' ? 'redstone_torch' : 'redstone_torch_off'),
  light: (p) => (p.lit === 'true' ? 7 : 0),
  hardness: 0,
  redstone: true,
  drops: 'redstone_torch',
  pushReaction: 'destroy',
  sound: 'wood',
});
def('repeater', {
  render: 'box',
  layer: 'cutout',
  props: [P_FACING4, intProp('delay', 1, 4), P_POWERED, boolProp('locked')],
  defaultProps: { facing: 'north', delay: '1', powered: 'false', locked: 'false' },
  tex: (p) => (p.powered === 'true' ? 'repeater_top_on' : 'repeater_top'),
  light: (p) => (p.powered === 'true' ? 7 : 0),
  hardness: 0,
  collides: false,
  redstone: true,
  drops: 'repeater',
  pushReaction: 'destroy',
  sound: 'wood',
});
def('comparator', {
  render: 'box',
  layer: 'cutout',
  props: [P_FACING4, enumProp('mode', ['compare', 'subtract']), P_POWERED],
  defaultProps: { facing: 'north', mode: 'compare', powered: 'false' },
  tex: (p) => (p.powered === 'true' ? 'comparator_top_on' : 'comparator_top'),
  light: (p) => (p.powered === 'true' ? 7 : 0),
  hardness: 0,
  redstone: true,
  entity: 'comparator',
  drops: 'comparator',
  pushReaction: 'destroy',
  sound: 'wood',
});
def('redstone_block', {
  render: 'cube',
  tex: 'redstone_block',
  solid: true,
  opaque: true,
  hardness: 5,
  resistance: 6,
  tool: 'pickaxe',
  requiresTool: true,
  redstone: true,
  sound: 'metal',
});
def('redstone_lamp', {
  render: 'cube',
  props: [P_LIT],
  tex: (p) => (p.lit === 'true' ? 'redstone_lamp_on' : 'redstone_lamp'),
  light: (p) => (p.lit === 'true' ? 15 : 0),
  solid: true,
  opaque: true,
  hardness: 0.3,
  redstone: true,
  drops: 'redstone_lamp',
  sound: 'glass',
});
def('lever', {
  render: 'box',
  layer: 'cutout',
  props: [P_FACING6, enumProp('face', ['floor', 'wall', 'ceiling']), P_POWERED],
  defaultProps: { facing: 'north', face: 'wall', powered: 'false' },
  tex: 'lever',
  hardness: 0.5,
  redstone: true,
  pushReaction: 'destroy',
  sound: 'wood',
});
for (const [name, tex, hardness] of [
  ['stone_button', 'stone', 0.5],
  ['oak_button', 'oak_planks', 0.5],
] as const) {
  def(name, {
    render: 'box',
    props: [P_FACING6, enumProp('face', ['floor', 'wall', 'ceiling']), P_POWERED],
    defaultProps: { facing: 'north', face: 'wall', powered: 'false' },
    tex,
    hardness,
    redstone: true,
    pushReaction: 'destroy',
    sound: name.startsWith('stone') ? 'stone' : 'wood',
  });
}
for (const [name, tex] of [
  ['stone_pressure_plate', 'stone'],
  ['oak_pressure_plate', 'oak_planks'],
  ['light_weighted_pressure_plate', 'gold_block'],
  ['heavy_weighted_pressure_plate', 'iron_block'],
] as const) {
  def(name, {
    render: 'box',
    props: [P_POWERED],
    tex,
    hardness: 0.5,
    redstone: true,
    pushReaction: 'destroy',
    sound: name.includes('oak') ? 'wood' : 'stone',
  });
}
def('piston', {
  render: 'box',
  props: [P_FACING6, boolProp('extended')],
  defaultProps: { facing: 'north', extended: 'false' },
  tex: (p) =>
    p.extended === 'true'
      ? facing6Tex(p.facing!, 'piston_inner', 'piston_bottom', 'piston_side')
      : facing6Tex(p.facing!, 'piston_top', 'piston_bottom', 'piston_side'),
  solid: true,
  opaque: false,
  hardness: 1.5,
  redstone: true,
  pushReaction: 'block',
  sound: 'stone',
});
def('sticky_piston', {
  render: 'box',
  props: [P_FACING6, boolProp('extended')],
  defaultProps: { facing: 'north', extended: 'false' },
  tex: (p) =>
    p.extended === 'true'
      ? facing6Tex(p.facing!, 'piston_inner', 'piston_bottom', 'piston_side')
      : facing6Tex(p.facing!, 'sticky_piston_top', 'piston_bottom', 'piston_side'),
  solid: true,
  opaque: false,
  hardness: 1.5,
  redstone: true,
  pushReaction: 'block',
  sound: 'stone',
});
def('piston_head', {
  render: 'box',
  props: [P_FACING6, enumProp('type', ['normal', 'sticky'])],
  defaultProps: { facing: 'north', type: 'normal' },
  tex: (p) => facing6Tex(p.facing!, p.type === 'sticky' ? 'sticky_piston_top' : 'piston_top', 'piston_inner', 'piston_side'),
  solid: false,
  collides: true,
  opaque: false,
  hardness: 1.5,
  redstone: true,
  drops: null,
  pushReaction: 'block',
  sound: 'stone',
});
def('moving_piston', {
  render: 'none',
  props: [P_FACING6, enumProp('type', ['normal', 'sticky'])],
  defaultProps: { facing: 'north', type: 'normal' },
  tex: 'piston_inner',
  solid: false,
  opaque: false,
  hardness: -1,
  entity: 'moving_piston',
  drops: null,
  pushReaction: 'block',
});
def('observer', {
  render: 'cube',
  props: [P_FACING6, P_POWERED],
  defaultProps: { facing: 'south', powered: 'false' },
  tex: (p) => observerTex(p.facing!),
  solid: true,
  opaque: true,
  hardness: 3,
  tool: 'pickaxe',
  requiresTool: true,
  redstone: true,
  pushReaction: 'block',
  sound: 'stone',
});
def('hopper', {
  render: 'box',
  props: [P_FACING6, boolProp('enabled')],
  defaultProps: { facing: 'down', enabled: 'true' },
  tex: { top: 'hopper_top', bottom: 'hopper_side', side: 'hopper_side' },
  solid: false,
  collides: true,
  opaque: false,
  hardness: 3,
  resistance: 4.8,
  tool: 'pickaxe',
  requiresTool: true,
  redstone: true,
  entity: 'hopper',
  sound: 'metal',
});
def('dispenser', {
  render: 'cube',
  props: [P_FACING6, boolProp('triggered')],
  defaultProps: { facing: 'north', triggered: 'false' },
  tex: (p) => facing6Tex(p.facing!, 'furnace_top', 'furnace_top', 'furnace_side', 'dispenser_front'),
  solid: true,
  opaque: true,
  hardness: 3.5,
  tool: 'pickaxe',
  requiresTool: true,
  redstone: true,
  entity: 'chest',
  sound: 'stone',
});
def('dropper', {
  render: 'cube',
  props: [P_FACING6, boolProp('triggered')],
  defaultProps: { facing: 'north', triggered: 'false' },
  tex: (p) => facing6Tex(p.facing!, 'furnace_top', 'furnace_top', 'furnace_side', 'dropper_front'),
  solid: true,
  opaque: true,
  hardness: 3.5,
  tool: 'pickaxe',
  requiresTool: true,
  redstone: true,
  entity: 'chest',
  sound: 'stone',
});
def('note_block', {
  render: 'cube',
  props: [intProp('note', 0, 24), P_POWERED, enumProp('instrument', ['harp', 'bass', 'snare', 'hat', 'basedrum'])],
  defaultProps: { note: '0', powered: 'false', instrument: 'harp' },
  tex: 'note_block',
  solid: true,
  opaque: true,
  hardness: 0.8,
  tool: 'axe',
  redstone: true,
  sound: 'wood',
});
def('daylight_detector', {
  render: 'box',
  props: [intProp('power', 0, 15), boolProp('inverted')],
  tex: { top: 'daylight_detector_top', bottom: 'daylight_detector_side', side: 'daylight_detector_side' },
  solid: false,
  collides: true,
  opaque: false,
  hardness: 0.2,
  tool: 'axe',
  redstone: true,
  sound: 'wood',
});
def('target', {
  render: 'cube',
  props: [intProp('power', 0, 15)],
  tex: 'target',
  solid: true,
  opaque: true,
  hardness: 0.5,
  redstone: true,
  sound: 'grass',
});
const RAIL_SHAPES = [
  'north_south', 'east_west', 'ascending_east', 'ascending_west', 'ascending_north',
  'ascending_south', 'south_east', 'south_west', 'north_west', 'north_east',
] as const;
def('rail', {
  render: 'rail',
  layer: 'cutout',
  props: [enumProp('shape', RAIL_SHAPES)],
  tex: 'rail',
  hardness: 0.7,
  tool: 'pickaxe',
  pushReaction: 'destroy',
  sound: 'metal',
});
for (const [name, off, on] of [
  ['powered_rail', 'powered_rail', 'powered_rail_on'],
  ['detector_rail', 'detector_rail', 'detector_rail'],
  ['activator_rail', 'activator_rail', 'activator_rail_on'],
] as const) {
  def(name, {
    render: 'rail',
    layer: 'cutout',
    props: [enumProp('shape', RAIL_SHAPES.slice(0, 6)), P_POWERED],
    tex: (p) => (p.powered === 'true' ? on : off),
    hardness: 0.7,
    tool: 'pickaxe',
    redstone: true,
    pushReaction: 'destroy',
    sound: 'metal',
  });
}

// --- shaped building blocks -----------------------------------------------
const slabAndStairs = (base: string, tex: TexInput, opts: BlockOptions) => {
  def(`${base}_slab`, {
    ...opts,
    render: 'box',
    props: [P_SLAB],
    defaultProps: { type: 'bottom' },
    tex,
    solid: false,
    collides: true,
    opaque: false,
  });
  def(`${base}_stairs`, {
    ...opts,
    render: 'box',
    props: [P_FACING4, P_HALF, P_STAIR_SHAPE],
    defaultProps: { facing: 'north', half: 'bottom', shape: 'straight' },
    tex,
    solid: false,
    collides: true,
    opaque: false,
  });
};
const stoneOpts: BlockOptions = { hardness: 2, resistance: 6, tool: 'pickaxe', requiresTool: true, sound: 'stone' };
const woodOpts: BlockOptions = { hardness: 2, resistance: 3, tool: 'axe', flammable: 5, sound: 'wood' };
slabAndStairs('stone', 'stone', stoneOpts);
slabAndStairs('cobblestone', 'cobblestone', stoneOpts);
slabAndStairs('stone_brick', 'stone_bricks', stoneOpts);
slabAndStairs('sandstone', { top: 'sandstone_top', bottom: 'sandstone_top', side: 'sandstone' }, stoneOpts);
slabAndStairs('brick', 'bricks', stoneOpts);
slabAndStairs('nether_brick', 'nether_bricks', stoneOpts);
slabAndStairs('quartz', 'quartz_block', stoneOpts);
slabAndStairs('purpur', 'purpur_block', stoneOpts);
for (const w of WOODS) slabAndStairs(w, `${w}_planks`, woodOpts);

for (const w of WOODS) {
  def(`${w}_fence`, {
    render: 'box',
    props: [...P_CONNECT],
    tex: `${w}_planks`,
    solid: false,
    collides: true,
    opaque: false,
    ...woodOpts,
  });
  def(`${w}_fence_gate`, {
    render: 'box',
    props: [P_FACING4, P_OPEN, P_POWERED],
    defaultProps: { facing: 'north', open: 'false', powered: 'false' },
    tex: `${w}_planks`,
    solid: false,
    collides: true,
    opaque: false,
    redstone: true,
    ...woodOpts,
  });
  def(`${w}_door`, {
    render: 'box',
    layer: 'cutout',
    props: [P_FACING4, P_DOOR_HALF, P_HINGE, P_OPEN, P_POWERED],
    defaultProps: { facing: 'north', half: 'lower', hinge: 'left', open: 'false', powered: 'false' },
    tex: `${w}_planks`,
    solid: false,
    collides: true,
    opaque: false,
    redstone: true,
    hardness: 3,
    tool: 'axe',
    sound: 'wood',
    pushReaction: 'destroy',
  });
  def(`${w}_trapdoor`, {
    render: 'box',
    layer: 'cutout',
    props: [P_FACING4, P_HALF, P_OPEN, P_POWERED],
    defaultProps: { facing: 'north', half: 'bottom', open: 'false', powered: 'false' },
    tex: `${w}_planks`,
    solid: false,
    collides: true,
    opaque: false,
    redstone: true,
    hardness: 3,
    tool: 'axe',
    sound: 'wood',
  });
}
def('iron_door', {
  render: 'box',
  layer: 'cutout',
  props: [P_FACING4, P_DOOR_HALF, P_HINGE, P_OPEN, P_POWERED],
  defaultProps: { facing: 'north', half: 'lower', hinge: 'left', open: 'false', powered: 'false' },
  tex: 'iron_block',
  solid: false,
  collides: true,
  opaque: false,
  redstone: true,
  hardness: 5,
  tool: 'pickaxe',
  requiresTool: true,
  sound: 'metal',
  pushReaction: 'destroy',
});
def('glass_pane', {
  render: 'box',
  layer: 'cutout',
  props: [...P_CONNECT],
  tex: 'glass',
  solid: false,
  collides: true,
  opaque: false,
  hardness: 0.3,
  drops: null,
  sound: 'glass',
});
def('iron_bars', {
  render: 'box',
  layer: 'cutout',
  props: [...P_CONNECT],
  tex: 'iron_block',
  solid: false,
  collides: true,
  opaque: false,
  hardness: 5,
  tool: 'pickaxe',
  requiresTool: true,
  sound: 'metal',
});

// --- portals / end ---------------------------------------------------------
def('nether_portal', {
  render: 'box',
  layer: 'translucent',
  props: [P_AXIS],
  defaultProps: { axis: 'x' },
  tex: 'nether_portal',
  light: 11,
  hardness: -1,
  solid: false,
  opaque: false,
  drops: null,
  pushReaction: 'block',
});
def('end_portal', {
  render: 'box',
  layer: 'opaque',
  tex: 'end_portal',
  light: 15,
  hardness: -1,
  solid: false,
  opaque: false,
  drops: null,
  pushReaction: 'block',
});
def('end_portal_frame', {
  render: 'box',
  props: [P_FACING4, boolProp('eye')],
  defaultProps: { facing: 'north', eye: 'false' },
  tex: { top: 'end_portal_frame_top', bottom: 'end_stone', side: 'end_portal_frame_side' },
  solid: false,
  collides: true,
  opaque: false,
  light: 1,
  hardness: -1,
  drops: null,
  pushReaction: 'block',
  sound: 'stone',
});
def('dragon_egg', {
  render: 'box',
  layer: 'cutout',
  tex: 'dragon_egg',
  solid: false,
  collides: true,
  opaque: false,
  light: 1,
  hardness: 3,
  gravity: true,
  sound: 'stone',
});
def('end_rod', {
  render: 'box',
  layer: 'cutout',
  props: [P_FACING6],
  defaultProps: { facing: 'up' },
  tex: 'end_rod',
  light: 14,
  hardness: 0,
  sound: 'wood',
});
def('purpur_pillar', {
  render: 'cube',
  props: [P_AXIS],
  defaultProps: { axis: 'y' },
  tex: (p) =>
    p.axis === 'y'
      ? { top: 'purpur_pillar_top', bottom: 'purpur_pillar_top', side: 'purpur_pillar' }
      : { top: 'purpur_pillar', bottom: 'purpur_pillar', side: 'purpur_pillar' },
  solid: true,
  opaque: true,
  hardness: 1.5,
  tool: 'pickaxe',
  requiresTool: true,
  sound: 'stone',
});
def('chorus_plant', {
  render: 'box',
  layer: 'cutout',
  props: [...P_CONNECT, boolProp('up'), boolProp('down')],
  tex: 'chorus_plant',
  solid: false,
  collides: true,
  opaque: false,
  hardness: 0.4,
  tool: 'axe',
  sound: 'wood',
});

// ---------------------------------------------------------------------------
// Texture helpers
// ---------------------------------------------------------------------------
function facingTex(facing: string, front: string, side: string, top: string, bottom: string) {
  const faces: Record<string, string> = { top, bottom, north: side, south: side, west: side, east: side };
  faces[facing] = front;
  return faces as TexInput;
}
/** `TexInput` names the vertical faces `top`/`bottom`; directions call them `up`/`down`. */
const TEX_FACE_KEY: Record<string, string> = {
  down: 'bottom',
  up: 'top',
  north: 'north',
  south: 'south',
  west: 'west',
  east: 'east',
};
const OPPOSITE_NAME: Record<string, string> = {
  down: 'up', up: 'down', north: 'south', south: 'north', west: 'east', east: 'west',
};

function facing6Tex(facing: string, front: string, back: string, side: string, altFront?: string) {
  const f = altFront ?? front;
  const faces: Record<string, string> = {
    bottom: side, top: side, north: side, south: side, west: side, east: side,
  };
  faces[TEX_FACE_KEY[facing]!] = f;
  faces[TEX_FACE_KEY[OPPOSITE_NAME[facing]!]!] = back;
  return faces as TexInput;
}

function observerTex(facing: string) {
  const faces: Record<string, string> = {
    bottom: 'observer_side', top: 'observer_side', north: 'observer_side',
    south: 'observer_side', west: 'observer_side', east: 'observer_side',
  };
  if (facing === 'up' || facing === 'down') {
    // Vertical observers show the "top" band on their four sides instead.
    faces.north = 'observer_top';
    faces.south = 'observer_top';
    faces.west = 'observer_top';
    faces.east = 'observer_top';
  } else {
    faces.top = 'observer_top';
    faces.bottom = 'observer_top';
  }
  faces[TEX_FACE_KEY[facing]!] = 'observer_front';
  faces[TEX_FACE_KEY[OPPOSITE_NAME[facing]!]!] = 'observer_back';
  return faces as TexInput;
}

// ---------------------------------------------------------------------------
// Finalisation: assign state ranges and build lookup tables
// ---------------------------------------------------------------------------

export const BLOCKS: readonly BlockDef[] = defs;
export const BLOCK_COUNT = defs.length;

let total = 0;
for (const d of defs) {
  d.firstState = total;
  total += d.stateCount;
}
export const TOTAL_STATES = total;
if (TOTAL_STATES > 65535) throw new Error(`too many block states: ${TOTAL_STATES}`);

/** Block id for each state. */
export const STATE_BLOCK = new Uint16Array(TOTAL_STATES);
/** Property value index per state, keyed by property name (-1 if absent). */
const PROP_TABLES = new Map<string, Int16Array>();
/** Per-state face texture layers, 6 per state. */
export const STATE_TEX = new Uint16Array(TOTAL_STATES * 6);
/** Per-state emitted light. */
export const STATE_LIGHT = new Uint8Array(TOTAL_STATES);
/** Per-state light attenuation (15 for opaque). */
export const STATE_FILTER = new Uint8Array(TOTAL_STATES);
/** Bitfield of cached per-state booleans. */
export const STATE_FLAGS = new Uint16Array(TOTAL_STATES);

export const F_SOLID = 1 << 0; // full-cube collision
export const F_OPAQUE = 1 << 1; // blocks light, culls faces
export const F_REPLACEABLE = 1 << 2;
export const F_LIQUID = 1 << 3;
export const F_COLLIDES = 1 << 4; // has any collision at all
export const F_CULL_SAME = 1 << 5;
export const F_AIR = 1 << 6;
export const F_REDSTONE = 1 << 7;
export const F_GRAVITY = 1 << 8;
export const F_CUTOUT = 1 << 9;
export const F_TRANSLUCENT = 1 << 10;
export const F_HAS_ENTITY = 1 << 11;
export const F_RENDER_CUBE = 1 << 12;
export const F_INVISIBLE = 1 << 13;

function propsOfState(d: BlockDef, local: number): Record<string, string> {
  const out: Record<string, string> = {};
  for (const p of d.propDefs) {
    out[p.name] = p.values[Math.floor(local / p.stride) % p.values.length]!;
  }
  return out;
}

function resolveTexFaces(input: TexInput): TexFaces {
  if (typeof input === 'string') {
    const id = texId(input);
    return { down: id, up: id, north: id, south: id, west: id, east: id };
  }
  const all = input.all ?? input.side;
  const side = input.side ?? all;
  const top = input.top ?? all;
  const bottom = input.bottom ?? top ?? all;
  const pick = (v: string | undefined, fallback: string | undefined) => {
    const name = v ?? fallback;
    if (!name) throw new Error(`incomplete texture spec: ${JSON.stringify(input)}`);
    return texId(name);
  };
  return {
    down: pick(input.bottom, bottom),
    up: pick(input.top, top),
    north: pick(input.north, side),
    south: pick(input.south, side),
    west: pick(input.west, side),
    east: pick(input.east, side),
  };
}

for (const d of defs) {
  // Default state index from defaultProps.
  let defaultLocal = 0;
  if (d.defaultProps) {
    for (const [k, v] of Object.entries(d.defaultProps)) {
      const p = d.propByName.get(k);
      if (!p) throw new Error(`block ${d.name} has no property ${k}`);
      const idx = p.values.indexOf(v);
      if (idx < 0) throw new Error(`block ${d.name} property ${k} has no value ${v}`);
      defaultLocal += idx * p.stride;
    }
  }
  d.defaultState = d.firstState + defaultLocal;

  for (const p of d.propDefs) {
    let table = PROP_TABLES.get(p.name);
    if (!table) {
      table = new Int16Array(TOTAL_STATES).fill(-1);
      PROP_TABLES.set(p.name, table);
    }
  }

  for (let local = 0; local < d.stateCount; local++) {
    const state = d.firstState + local;
    STATE_BLOCK[state] = d.id;
    const props = propsOfState(d, local);

    for (const p of d.propDefs) {
      const table = PROP_TABLES.get(p.name)!;
      table[state] = Math.floor(local / p.stride) % p.values.length;
    }

    const texInput = typeof d.tex === 'function' ? d.tex(props) : d.tex;
    if (texInput) {
      const faces = resolveTexFaces(texInput);
      const base = state * 6;
      STATE_TEX[base + Dir.DOWN] = faces.down;
      STATE_TEX[base + Dir.UP] = faces.up;
      STATE_TEX[base + Dir.NORTH] = faces.north;
      STATE_TEX[base + Dir.SOUTH] = faces.south;
      STATE_TEX[base + Dir.WEST] = faces.west;
      STATE_TEX[base + Dir.EAST] = faces.east;
    }

    STATE_LIGHT[state] = typeof d.light === 'function' ? d.light(props) : (d.light ?? 0);
    STATE_FILTER[state] = d.opaque ? 15 : (d.filter ?? 0);

    let flags = 0;
    if (d.solid) flags |= F_SOLID | F_COLLIDES;
    if (d.collides) flags |= F_COLLIDES;
    if (d.opaque) flags |= F_OPAQUE;
    if (d.replaceable) flags |= F_REPLACEABLE;
    if (d.liquid) flags |= F_LIQUID;
    if (d.cullSame) flags |= F_CULL_SAME;
    if (d.redstone) flags |= F_REDSTONE;
    if (d.gravity) flags |= F_GRAVITY;
    if (d.entity) flags |= F_HAS_ENTITY;
    if (d.layer === 'cutout') flags |= F_CUTOUT;
    if (d.layer === 'translucent') flags |= F_TRANSLUCENT;
    if (d.render === 'cube') flags |= F_RENDER_CUBE;
    if (d.render === 'none' || d.render === undefined) flags |= F_INVISIBLE;
    if (d.name === 'air' || d.name === 'cave_air' || d.name === 'void_air') flags |= F_AIR;
    STATE_FLAGS[state] = flags;
  }
}

// ---------------------------------------------------------------------------
// Public accessors
// ---------------------------------------------------------------------------

export function blockDef(name: string): BlockDef {
  const d = byName.get(name);
  if (!d) throw new Error(`unknown block: ${name}`);
  return d;
}

export function blockDefById(id: number): BlockDef {
  return defs[id]!;
}

export function blockOf(state: number): BlockDef {
  return defs[STATE_BLOCK[state]!]!;
}

export function blockNameOf(state: number): string {
  return defs[STATE_BLOCK[state]!]!.name;
}

/** Default state for a block name. Throws for unknown names. */
export function stateOf(name: string): number {
  return blockDef(name).defaultState;
}

/** Build a state from a block name and explicit property values. */
export function stateWith(name: string, props: Record<string, string | number | boolean>): number {
  const d = blockDef(name);
  let local = 0;
  for (const [k, raw] of Object.entries(props)) {
    const p = d.propByName.get(k);
    if (!p) throw new Error(`block ${name} has no property ${k}`);
    const v = String(raw);
    const idx = p.values.indexOf(v);
    if (idx < 0) throw new Error(`block ${name}.${k} has no value ${v} (have ${p.values.join(',')})`);
    local += idx * p.stride;
  }
  // Fill unspecified properties from the default state.
  for (const p of d.propDefs) {
    if (!(p.name in props)) {
      const defIdx = Math.floor((d.defaultState - d.firstState) / p.stride) % p.values.length;
      local += defIdx * p.stride;
    }
  }
  return d.firstState + local;
}

/** Read a property's string value, or undefined if the block lacks it. */
export function getProp(state: number, name: string): string | undefined {
  const table = PROP_TABLES.get(name);
  if (!table) return undefined;
  const idx = table[state]!;
  if (idx < 0) return undefined;
  return defs[STATE_BLOCK[state]!]!.propByName.get(name)!.values[idx];
}

/** Read a property's *value index*, which for int properties is the value itself. */
export function getPropIndex(state: number, name: string): number {
  const table = PROP_TABLES.get(name);
  if (!table) return -1;
  return table[state]!;
}

export function getBool(state: number, name: string): boolean {
  return getPropIndex(state, name) === 1;
}

/** Numeric properties declared with `intProp(name, min, max)` start at `min`. */
export function getInt(state: number, name: string): number {
  const d = defs[STATE_BLOCK[state]!]!;
  const p = d.propByName.get(name);
  if (!p) return -1;
  const idx = PROP_TABLES.get(name)![state]!;
  return Number(p.values[idx]);
}

/** Return a new state with one property changed. */
export function withProp(state: number, name: string, value: string | number | boolean): number {
  const d = defs[STATE_BLOCK[state]!]!;
  const p = d.propByName.get(name);
  if (!p) return state;
  const table = PROP_TABLES.get(name)!;
  const current = table[state]!;
  const next = p.values.indexOf(String(value));
  if (next < 0) throw new Error(`block ${d.name}.${name} has no value ${value}`);
  return state + (next - current) * p.stride;
}

export function withBool(state: number, name: string, value: boolean): number {
  return withProp(state, name, value ? 'true' : 'false');
}

export function hasProp(state: number, name: string): boolean {
  const table = PROP_TABLES.get(name);
  return !!table && table[state]! >= 0;
}

export function getFacing(state: number): Direction {
  const idx = getPropIndex(state, 'facing');
  if (idx < 0) return Dir.NORTH;
  const d = defs[STATE_BLOCK[state]!]!;
  const p = d.propByName.get('facing')!;
  // FACING4 values map to Dir.NORTH..Dir.EAST (2..5); FACING6 maps directly.
  return (p.values.length === 4 ? idx + 2 : idx) as Direction;
}

export function withFacing(state: number, dir: Direction): number {
  const d = defs[STATE_BLOCK[state]!]!;
  const p = d.propByName.get('facing');
  if (!p) return state;
  const name = p.values.length === 4 ? FACING4[dir - 2] : FACING6[dir];
  if (!name) return state;
  return withProp(state, 'facing', name);
}

// Fast flag helpers -- these are on the hot path of meshing and physics.
export const isAir = (s: number): boolean => (STATE_FLAGS[s]! & F_AIR) !== 0;
export const isOpaque = (s: number): boolean => (STATE_FLAGS[s]! & F_OPAQUE) !== 0;
export const isSolid = (s: number): boolean => (STATE_FLAGS[s]! & F_SOLID) !== 0;
export const collides = (s: number): boolean => (STATE_FLAGS[s]! & F_COLLIDES) !== 0;
export const isReplaceable = (s: number): boolean => (STATE_FLAGS[s]! & F_REPLACEABLE) !== 0;
export const isLiquid = (s: number): boolean => (STATE_FLAGS[s]! & F_LIQUID) !== 0;
export const lightEmission = (s: number): number => STATE_LIGHT[s]!;
export const lightFilter = (s: number): number => STATE_FILTER[s]!;
export const faceTexture = (s: number, dir: Direction): number => STATE_TEX[s * 6 + dir]!;
export const hasBlockEntity = (s: number): boolean => (STATE_FLAGS[s]! & F_HAS_ENTITY) !== 0;

export const AIR_STATE = 0;
export const WATER_STATE = stateOf('water');
export const LAVA_STATE = stateOf('lava');

/** Block names in registration order — used by the save format's id remapping. */
export const BLOCK_NAMES: readonly string[] = defs.map((d) => d.name);
